"use client";

import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import EHRAppointmentRemindersCard from "@/src/components/cards/ehr-cards/ehr-appointment-reminders-card";
import EHRBookedAppointmentsCard from "@/src/components/cards/ehr-cards/ehr-booked-appointments-card";
import EHRDocumentCard from "@/src/components/cards/ehr-cards/ehr-document-card";
import LocationCard from "@/src/components/cards/location-card/location-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import PractitionerCard from "@/src/components/cards/practitioner-card/practitioner-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { ReferralEligibilityCard } from "@/src/components/tasks/referral-eligibility-card";
import { TaskStepCard } from "@/src/components/tasks/task-step-card";
import {
  useReferralLocationSwitchHandler,
  useReferralPractitionerSwitchHandler,
} from "@/src/hooks/use-entity-switch-handlers";
import { resolveStediEligibilityDisplay } from "@/src/lib/clearinghouse/stedi/extract-eligibility-summary";
import { useStediEligibilityCheck } from "@/src/lib/data-fetchers/stedi-eligibility-check-fetchers";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { Appointment } from "@/src/models/appointment";
import { Coverage } from "@/src/models/coverage";
import { Referral } from "@/src/models/documents/referral";
import { EHRDocument } from "@/src/models/ehr-document";
import { Patient } from "@/src/models/patient";
import { Practitioner } from "@/src/models/practitioner";
import { StediEligibilityStatusValue } from "@/src/models/stedi-eligibility-status";
import { TaskStepCardStatus } from "@/src/models/task-step-card";

export interface AthenaReferralCardsData {
  patientDetails: Patient | null;
  coverages: Coverage[];
  careTeam: Practitioner[];
  appointmentReminders: Appointment[];
  bookedAppointments: Appointment[];
  document: EHRDocument | null;
  expectedDocument: EHRDocument | null;
  documentPatientId?: string | null;
}

interface AthenaReferralCardsProps {
  referral: Referral;
  athenaData: AthenaReferralCardsData | null;
  taskId?: string;
  insuranceVerificationTaskId?: string;
}

export function AthenaReferralCards({
  referral,
  athenaData,
  taskId,
  insuranceVerificationTaskId,
}: AthenaReferralCardsProps) {
  const { eligibilityCheck } = useStediEligibilityCheck(referral.id, referral.primaryCoverage);
  const { eligibilityCheck: secondaryCheck } = useStediEligibilityCheck(referral.id, referral.secondaryCoverage);
  const secondaryStatus = secondaryCheck ? resolveStediEligibilityDisplay(secondaryCheck).eligibilityStatus : undefined;
  const secondaryEligibilityStatus =
    secondaryStatus === StediEligibilityStatusValue.ACTIVE || secondaryStatus === StediEligibilityStatusValue.INACTIVE
      ? secondaryStatus
      : undefined;
  const status = eligibilityCheck ? resolveStediEligibilityDisplay(eligibilityCheck).eligibilityStatus : undefined;
  const eligibilityStatus = status === "active" || status === "inactive" ? status : undefined;
  const { createLocationSwitchHandler, isPending: isLocationPending } = useReferralLocationSwitchHandler();
  const { createPractitionerSwitchHandler, isPending: isPractitionerPending } = useReferralPractitionerSwitchHandler();

  const primaryCoverage = referral.primaryCoverage;
  const secondaryCoverage = referral.secondaryCoverage;

  const ehrCoverages = [...(athenaData?.coverages || [])].sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  const primaryEhrCoverage = ehrCoverages.find((cov) => cov.order === 1) ?? ehrCoverages[0] ?? null;
  const secondaryEhrCoverage = ehrCoverages.find((cov) => cov.order === 2) ?? ehrCoverages[1] ?? null;

  const insuranceCards = (
    <div className="space-y-8">
      {primaryCoverage && (
        <CoverageCard
          key="coverage-primary"
          coverage={primaryCoverage}
          type="Primary"
          compareMode={true}
          secondaryCoverage={primaryEhrCoverage}
          isEditable={true}
          eligibilityStatus={eligibilityStatus}
        />
      )}
      {secondaryCoverage && (
        <CoverageCard
          key="coverage-secondary"
          coverage={secondaryCoverage}
          type="Secondary"
          compareMode={true}
          secondaryCoverage={secondaryEhrCoverage}
          eligibilityStatus={secondaryEligibilityStatus}
          isEditable={true}
        />
      )}
    </div>
  );

  return (
    <>
      <EHRDocumentCard
        document={athenaData?.document || null}
        expectedDocument={athenaData?.expectedDocument || null}
      />

      <PatientCard patient={referral.patient} compareMode secondaryPatient={athenaData?.patientDetails} />

      {insuranceVerificationTaskId ? (
        <TaskStepCard
          header="Insurance"
          status={TaskStepCardStatus.VERIFICATION_REQUIRED}
          detail="Compare the primary and secondary insurance against Athena and the referral document, then mark the insurance correct or incorrect."
          taskId={insuranceVerificationTaskId}
          stepKey="insurance"
          workflowStepKeys={[StepKey.CHECK_INSURANCE_ELIGIBILITY, StepKey.CREATE_REFERRAL]}
        >
          {insuranceCards}
          {!primaryCoverage && <p className="text-sm text-muted-foreground">No primary insurance is recorded.</p>}
        </TaskStepCard>
      ) : (
        insuranceCards
      )}

      {taskId && (
        <ReferralEligibilityCard
          referralId={referral.id}
          taskId={taskId}
          primaryCoverage={referral.primaryCoverage}
          secondaryCoverage={referral.secondaryCoverage}
        />
      )}

      {referral.referringProvider && (
        <PractitionerCard
          practitioner={referral.referringProvider}
          type="Referring"
          compareMode={true}
          secondaryPractitioners={athenaData?.careTeam || null}
          onEditPractitioner={createPractitionerSwitchHandler(referral, "referringProvider")}
          onEditPractitionerLoading={isPractitionerPending}
          isEditable={true}
        />
      )}

      {referral.referringLocation && (
        <LocationCard
          location={referral.referringLocation}
          type="Referring"
          onEditLocation={createLocationSwitchHandler(referral, "referringLocation")}
          onEditLocationLoading={isLocationPending}
          isEditable={true}
        />
      )}

      {referral.receivingProvider && (
        <PractitionerCard
          practitioner={referral.receivingProvider}
          type="Receiving"
          compareMode={false}
          onEditPractitioner={createPractitionerSwitchHandler(referral, "receivingProvider")}
          onEditPractitionerLoading={isPractitionerPending}
          isEditable={true}
        />
      )}

      {referral.receivingLocation && (
        <LocationCard
          location={referral.receivingLocation}
          type="Receiving"
          onEditLocation={createLocationSwitchHandler(referral, "receivingLocation")}
          onEditLocationLoading={isLocationPending}
          isEditable={true}
        />
      )}

      <ReferralCard referral={referral} isEditable />

      <EHRAppointmentRemindersCard appointmentReminders={athenaData?.appointmentReminders || []} />

      <EHRBookedAppointmentsCard bookedAppointments={athenaData?.bookedAppointments || []} />
    </>
  );
}
